/**
 * Tenant-only roster Control Centre optimisation rollout flags.
 *
 * These keys are independently reversible and fail closed. A missing row, a
 * global-only row, or a helper/read error resolves to disabled. They must
 * never fall back to plan or global defaults.
 *
 * Exact tenant rows, including Medea Park and Golden Bloom, can enable the
 * four keys. Dispatch-reliability flags remain separately Medea-contained.
 *
 * Classification (Phase 1A) and usable-cover (Phase 1B) correctness are not
 * gated here — hiding present defects behind a UI flag would preserve wrong
 * current results.
 */

export const ROSTER_HORIZON_FLAG_DB_KEYS = {
  HORIZON: 'roster_horizon_v1',
  COMPACT_GRID: 'roster_compact_grid_v1',
  HORIZON_PUBLISH: 'roster_horizon_publish_v1',
  INLINE_ABSENCE: 'roster_inline_absence_v1',
} as const;

export type RosterHorizonFlagKey = keyof typeof ROSTER_HORIZON_FLAG_DB_KEYS;

/** Presentation-only controls. Disabling either restores the existing grid. */
export const ROSTER_HORIZON_PRESENTATION_FLAG_KEYS = [
  'HORIZON',
  'COMPACT_GRID',
] as const satisfies readonly RosterHorizonFlagKey[];

export type RosterHorizonPresentationFlagKey =
  (typeof ROSTER_HORIZON_PRESENTATION_FLAG_KEYS)[number];

export const ROSTER_HORIZON_PRESENTATION_FLAG_DB_KEYS = [
  ROSTER_HORIZON_FLAG_DB_KEYS.HORIZON,
  ROSTER_HORIZON_FLAG_DB_KEYS.COMPACT_GRID,
] as const;

export type RosterHorizonPresentationFlagDbKey =
  (typeof ROSTER_HORIZON_PRESENTATION_FLAG_DB_KEYS)[number];

/** Action-availability controls. These are never presentation rollback flags. */
export const ROSTER_HORIZON_ACTION_FLAG_KEYS = [
  'HORIZON_PUBLISH',
  'INLINE_ABSENCE',
] as const satisfies readonly RosterHorizonFlagKey[];

export type RosterHorizonActionFlagKey = (typeof ROSTER_HORIZON_ACTION_FLAG_KEYS)[number];

const ROSTER_HORIZON_FLAG_DB_KEY_SET = new Set<string>(Object.values(ROSTER_HORIZON_FLAG_DB_KEYS));

export const ROSTER_HORIZON_FLAG_KEYS = Object.keys(
  ROSTER_HORIZON_FLAG_DB_KEYS,
) as RosterHorizonFlagKey[];

export type RosterHorizonFlags = Record<RosterHorizonFlagKey, boolean>;

export const ROSTER_HORIZON_FLAGS_DEFAULT: RosterHorizonFlags = {
  HORIZON: false,
  COMPACT_GRID: false,
  HORIZON_PUBLISH: false,
  INLINE_ABSENCE: false,
};

export function isRosterHorizonFlagDbKey(flagKey: string | null | undefined): boolean {
  return ROSTER_HORIZON_FLAG_DB_KEY_SET.has(String(flagKey ?? '').trim());
}

export function isRosterHorizonFlagEnabled(
  flags: Partial<RosterHorizonFlags> | null | undefined,
  key: RosterHorizonFlagKey,
): boolean {
  return Boolean(flags?.[key]);
}

/**
 * Horizon keys are no longer hard-forced off for Medea Park. An exact tenant
 * row can enable them. Kept as an exported predicate so settings/admin
 * surfaces and tests share one answer. Dispatch-reliability containment is
 * `isContainedDispatchReliabilityEnablement`, not this function.
 */
export function isMedeaRosterHorizonEnablement(
  _flagKey: string | null | undefined,
  _tenantId: string | null | undefined,
): boolean {
  return false;
}

export function assertInlineAbsenceEnabled(
  flags: Partial<RosterHorizonFlags> | null | undefined,
): { ok: true } | { ok: false; error: string } {
  if (!isRosterHorizonFlagEnabled(flags, 'INLINE_ABSENCE')) {
    return {
      ok: false,
      error: 'Inline absence is not enabled for this tenant.',
    };
  }
  return { ok: true };
}

export function assertHorizonPublishIntentAllowed(
  flags: Partial<RosterHorizonFlags> | null | undefined,
  intent: 'publish' | 'request',
  requestedCount: number,
): { ok: true } | { ok: false; error: string } {
  if (
    (intent === 'request' || requestedCount > 1) &&
    !isRosterHorizonFlagEnabled(flags, 'HORIZON_PUBLISH')
  ) {
    return {
      ok: false,
      error: 'Selected publication and publication requests are not enabled for this tenant.',
    };
  }
  return { ok: true };
}

export function resolveRosterHorizonFlagsFromRows(input: {
  tenantId: string;
  rows?: Array<{ flag_key: string; tenant_id: string | null; enabled: boolean }> | null;
  error?: unknown;
}): RosterHorizonFlags {
  if (!input.tenantId || input.error) {
    return { ...ROSTER_HORIZON_FLAGS_DEFAULT };
  }

  const resolved = { ...ROSTER_HORIZON_FLAGS_DEFAULT };
  for (const key of ROSTER_HORIZON_FLAG_KEYS) {
    const dbKey = ROSTER_HORIZON_FLAG_DB_KEYS[key];
    const tenantRow = (input.rows ?? []).find(
      (row) => row.flag_key === dbKey && row.tenant_id === input.tenantId,
    );
    resolved[key] = Boolean(tenantRow?.enabled);
  }
  return resolved;
}
